import cloneLib from 'clone';
import { isDeepEqual, isObjectType, keys } from 'remeda';

export {
  entries,
  fromEntries,
  isEmptyish,
  isObjectType,
  isPlainObject,
  keys,
  mergeDeep,
  omit,
  values,
} from 'remeda';

/**
 * Deep clones an object with high performance.
 * See https://medium.com/@tiagobertolo/which-is-the-best-method-for-deep-cloning-in-javascript-are-they-all-bad-101f32d620c5
 * @param object - The object to clone
 * @returns The cloned object
 */
export const clone = <T>(object: T) => cloneLib(object);

/**
 * Retrieves a deeply nested property from an object or array using dot notation and/or bracket notation.
 *
 * This implementation handles bracket notation for arrays without relying on remeda's
 * discouraged `stringToPath` utility, which is intended only for legacy code migration.
 *
 * @param object - The object or array from which to retrieve the property.
 * @param nestedProperty - The path to the nested property, using dots and/or brackets.
 * @return The value of the nested property.
 * @example
 * // Object access
 * getNestedProperty({foo: {bar: { baz: 'hello'}}}, 'foo.bar.baz')
 * // returns 'hello'
 *
 * getNestedProperty({users: [{name: 'John'}, {name: 'Jane'}]}, 'users[1].name')
 * // returns 'Jane'
 *
 * getNestedProperty({data: {items: ['a', 'b', 'c']}}, 'data.items[2]')
 * // returns 'c'
 *
 * // Array as root object
 * getNestedProperty([{name: 'John'}, {name: 'Jane'}], '[1].name')
 * // returns 'Jane'
 *
 * getNestedProperty([[{value: 'a'}], [{value: 'b'}]], '[1][0].value')
 * // returns 'b'
 */
export const getNestedProperty = <T>(
  object: Record<string, unknown> | unknown[],
  nestedProperty: string,
): T | undefined => {
  // Handle empty path
  if (!nestedProperty) {
    return object as T;
  }

  // Parse the path to handle both dot notation and bracket notation
  // Convert bracket notation to dot notation, then split by dots
  const segments = nestedProperty
    .replace(/\[(\d+)\]/g, '.$1') // Convert [index] to .index
    .split('.')
    .filter((segment) => segment !== ''); // Remove empty segments

  // Traverse the nested path by reducing over segments
  return segments.reduce<unknown>(
    (current, segment) => current?.[segment],
    object,
  ) as T | undefined;
};

/**
 * Recursively removes keys from an object and its nested objects if their value matches any of the specified values.
 *
 * @param object - The object to process.
 * @param valuesToRemove - An array of values that trigger key removal. Objects and arrays match by deep equality, so `[]` removes keys holding an empty array.
 * @returns A new object with keys removed if their values are in valuesToRemove.
 * @example withoutObjectKeysWhereValueIs({ foo: null, bar: 'hello', baz: undefined }, [null, undefined]) // returns { bar: 'hello' }
 * @example withoutObjectKeysWhereValueIs({ ids: [], name: 'a' }, [[]]) // returns { name: 'a' }
 */
export const withoutObjectKeysWhereValueIs = <
  T extends Record<string, unknown>,
>(
  object: T,
  valuesToRemove: unknown[],
): Partial<T> => {
  const removeNestedKeys = (object_: unknown): unknown => {
    if (!isObjectType(object_) || object_ === null) {
      return object_;
    }

    if (Array.isArray(object_)) {
      return object_.map((item) => removeNestedKeys(item));
    }

    const newObject: Record<string, unknown> = {};
    for (const key of keys(object_)) {
      if (!Object.hasOwn(object_, key)) {
        continue;
      }

      const value = object_[key];

      if (
        valuesToRemove.some((valueToRemove) =>
          isDeepEqual(valueToRemove, value),
        )
      ) {
        continue; // Skip this key
      }

      if (isObjectType(value)) {
        const newValue = removeNestedKeys(value);
        newObject[key] = newValue; // Assign the modified value
      } else {
        newObject[key] = value; // Keep the original value
      }
    }

    return newObject;
  };

  return removeNestedKeys(object);
};

/** Compares two objects/arrays for content equality, regardless of property/element order.
 *
 * @param objectOne - First object/array to compare
 * @param objectTwo - Second object/array to compare
 * @returns True if contents are equal, false otherwise
 */
/**
 * Converts a value into a canonical, order-independent form:
 * - Object keys are sorted alphabetically at every nesting level.
 * - Array elements are sorted by their canonical JSON representation.
 *
 * This allows two values to be compared for semantic equality regardless
 * of insertion order.
 */
const toCanonical = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    const normalized = value.map(toCanonical);
    return [...normalized].sort((a, b) =>
      JSON.stringify(a).localeCompare(JSON.stringify(b)),
    );
  }

  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([k, v]) => [k, toCanonical(v)]),
    );
  }

  return value;
};

export const isObjectContentEqual = (
  objectOne: unknown,
  objectTwo: unknown,
): boolean =>
  JSON.stringify(toCanonical(objectOne)) ===
  JSON.stringify(toCanonical(objectTwo));

/**
 * Maps values for specified keys in an object and its nested objects.
 *
 * @param object - The object to process.
 * @param keys - Array of keys (or path patterns) to transform.
 * @param mapFn - Transformation function.
 * @returns A new object with transformed values.
 */
export const mapValueForKeys = <T>(
  object: T,
  keys: string[],
  mapFunction: (key: string, value: unknown, path: string) => unknown,
): T => {
  const processValue = (value: unknown, currentPath = ''): unknown => {
    // Handle null/undefined
    if (value === null || value === undefined) {
      return value;
    }

    // Handle arrays
    if (Array.isArray(value)) {
      return value.map((item, index) => {
        const itemPath = `${currentPath}[${index}]`;

        return processValue(item, itemPath);
      });
    }

    // Handle non-objects
    if (typeof value !== 'object') {
      return value;
    }

    // File/Blob objects cannot be deep cloned (their properties like `size` are read-only getters)
    // Return them as-is since they are leaf values that don't need key mapping
    if (value instanceof Blob) {
      return value;
    }

    // Shallow clone to avoid modifying original.
    // Deep cloning is not needed since nested values are handled recursively below.
    // This also avoids issues with File/Blob objects nested inside, which cannot be deep cloned.
    const result = { ...(value as Record<string, unknown>) };

    // Process each property
    for (const [key, propertyValue] of Object.entries(result)) {
      const propertyPath = currentPath ? `${currentPath}.${key}` : key;

      // Check if this key should be transformed
      const shouldTransform = keys.some(
        (targetKey) =>
          key === targetKey ||
          propertyPath === targetKey ||
          propertyPath.endsWith(`.${targetKey}`) ||
          // react-doctor-disable-next-line react-doctor/js-set-map-lookups
          propertyPath.includes(`[${targetKey}]`) ||
          // react-doctor-disable-next-line react-doctor/js-set-map-lookups
          propertyPath.includes(`.${targetKey}.`) ||
          // react-doctor-disable-next-line react-doctor/js-set-map-lookups
          propertyPath.includes(`.${targetKey}[`),
      );

      if (shouldTransform) {
        result[key] = mapFunction(key, propertyValue, propertyPath);
      } else if (propertyValue && typeof propertyValue === 'object') {
        // Recursively process nested objects and arrays
        result[key] = processValue(propertyValue, propertyPath);
      }
    }

    return result;
  };

  return processValue(object) as T;
};
